/**
 * ProductPortfolioTab Component
 *
 * Renders the Product Portfolio tab content for CompanyDetails, including:
 * - Key products card with product name/description list
 * - Key end-markets card with business vertical name/description list
 * - Keywords card with ChipsList for product keywords
 *
 * CALCULATION OWNERSHIP: This component receives all data from parent.
 * Loading and error states are determined by parent component.
 */

import GeneralLoader from '../../../loaders/GeneralLoader';
import ChipsList from '../../../common/ChipsList';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import { FIELD_SOURCE, dataSourceClass } from '../../../../utils/constants/dataSources';

/**
 * @param {Object} props
 * @param {Array} props.keyProducts - List of key products [{name, description}]
 * @param {Array} props.businessVerticals - List of business verticals [{name, description}]
 * @param {Array} props.products - List of product keywords for ChipsList
 * @param {boolean} props.isLoading - Whether supplementary data is loading
 * @param {boolean} props.hasError - Whether there was an error loading data
 */
const ProductPortfolioTab = ({
  keyProducts = [],
  businessVerticals = [],
  products = [],
  isLoading = false,
  hasError = false
}) => {
  const showLoader = isLoading || hasError;
  const hasKeyProducts = keyProducts?.length > 0;
  const hasBusinessVerticals = businessVerticals?.length > 0;
  const hasProducts = products?.length > 0;

  return (
    <div className="product-portfolio-tab-cards">
      {/* Key Products Card */}
      <div className="card">
        <div className={`property ${dataSourceClass(FIELD_SOURCE.keyProducts)}`}>Key products</div>
        <div className="value">
          {showLoader ? (
            <GeneralLoader />
          ) : (
            <>
              {hasKeyProducts ? (
                <ul className="default">
                  {keyProducts.map((KP) => (
                    <li key={KP.name}>
                      <strong>{KP.name}</strong>: {KP.description}
                    </li>
                  ))}
                </ul>
              ) : (
                PUNCTUATION.EMPTY_VALUE
              )}
            </>
          )}
        </div>
      </div>

      {/* Key End-Markets Card */}
      <div className="card">
        <div className={`property ${dataSourceClass(FIELD_SOURCE.businessVerticals)}`}>
          Key end-markets
        </div>
        <div className="value">
          {showLoader ? (
            <GeneralLoader />
          ) : (
            <>
              {hasBusinessVerticals ? (
                <ul className="default">
                  {businessVerticals.map((BV) => (
                    <li key={BV.name}>
                      <strong>{BV.name}</strong>: {BV.description}
                    </li>
                  ))}
                </ul>
              ) : (
                PUNCTUATION.EMPTY_VALUE
              )}
            </>
          )}
        </div>
      </div>

      {/* Keywords Card */}
      <div className="card">
        <div className={`property ${dataSourceClass(FIELD_SOURCE.products)}`}>Keywords</div>
        <div className="value">
          {showLoader ? (
            <GeneralLoader />
          ) : (
            <>
              {hasProducts ? (
                <ChipsList
                  list={products}
                  visibleCount={88888}
                  listClassName="product-portfolio-keywords"
                />
              ) : (
                PUNCTUATION.EMPTY_VALUE
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductPortfolioTab;
